import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../ThemeContext';


export default function AuthorIdentitySwitcher({
  currentUser,
  authors,
  primaryAuthorId,
  onSelectAuthor,
  onNavigateToProfile,
  onDisconnect
}) {
  const { t, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const hasMultiple = authors?.length > 1;

  // Wallet connected but author data still loading
  if (!currentUser) {
    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={onNavigateToProfile}
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.borderStrong}`,
            borderRadius: '6px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            fontWeight: '500',
            color: t.textSecondary,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderStrong}
        >
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: t.buttonBg,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }} />
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
          <span>Connected</span>
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => hasMultiple ? setIsOpen(!isOpen) : onNavigateToProfile()}
        style={{
          backgroundColor: t.surface,
          border: `1px solid ${t.borderStrong}`,
          borderRadius: '6px',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
          color: t.text,
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderStrong}
      >
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: t.buttonBg,
          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
        }} />
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <span>{currentUser.name}</span>
        {hasMultiple && <ChevronDown size={16} />}
      </button>

      {isOpen && hasMultiple && (
        <>
          <div onClick={() => setIsOpen(false)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998
          }} />
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            backgroundColor: t.modalBg,
            border: `1px solid ${t.borderStrong}`,
            borderRadius: '6px',
            padding: '8px',
            minWidth: '200px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 999
          }}>
            <button
              onClick={() => { onNavigateToProfile(); setIsOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: `1px solid ${t.borderStrong}`,
                borderRadius: '4px',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: t.text,
                marginBottom: '8px'
              }}
            >
              View Wallet
            </button>
            
            {authors?.map((author) => (
              <button
                key={author.id}
                onClick={() => { onSelectAuthor(author); setIsOpen(false); }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: author.id === currentUser.id ? t.accentSubtle : 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  fontWeight: author.id === currentUser.id ? '600' : '400',
                  color: t.text,
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <span>{author.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {author.id === primaryAuthorId && (
                    <span style={{
                      backgroundColor: t.buttonBg,
                      color: t.buttonText,
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '10px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Primary
                    </span>
                  )}
                  {author.id === currentUser.id && (
                    <span style={{ fontSize: '12px', color: t.textSecondary }}>•</span>
                  )}
                </div>
              </button>
            ))}

            {/* Disconnect */}
            <button
              onClick={() => { onDisconnect(); setIsOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                borderTop: `1px solid ${t.borderStrong}`,
                borderRadius: '4px',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                fontWeight: '500',
                color: '#dc2626',
                marginTop: '8px',
                paddingTop: '14px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.08)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Disconnect Wallet
            </button>
          </div>
        </>
      )}
    </div>
  );
}